import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Gift, Plus, Trash2, LogOut, Copy, Check, Edit, Loader2, ExternalLink, Bell, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import { getCategoryIcon } from "../utils/categoryIcons";

// Constants
const TOAST_LONG_DURATION = 6000;

const CATEGORIES = [
  "Vêtements",
  "Chaussures",
  "Électronique",
  "Livres",
  "Jouets",
  "Décoration",
  "Sport",
  "Autre"
];

const CLOTHING_CATEGORIES = ["Vêtements", "Chaussures"];

const PRIORITY_LABELS = {
  1: { label: "Un petit kiff 😊", color: "bg-muted text-muted-foreground" },
  2: { label: "Ça me ferait plaisir 😄", color: "bg-accent text-accent-foreground" },
  3: { label: "J'en rêve 🔥", color: "bg-primary text-primary-foreground" }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [wishes, setWishes] = useState([]);
  const [filteredWishes, setFilteredWishes] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWish, setEditingWish] = useState(null);
  const [copied, setCopied] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [linkPreview, setLinkPreview] = useState(null);
  const [notifications, setNotifications] = useState([]);
  
  const [formData, setFormData] = useState({
    item_name: "",
    category: "",
    size: "",
    priority: null,
    link: "",
    og_image: null,
    og_title: null,
    og_price: null
  });

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    if (user) {
      fetchWishes();
      fetchNotifications();
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchWishes();
      fetchNotifications();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchWishes = async () => {
    try {
      const response = await axios.get(`${API}/wishes`, { withCredentials: true });
      setWishes(response.data);
      setFilteredWishes(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des souhaits");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data);
    } catch (error) {
      // Notifications fetch failed - non-critical, continue silently
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, { withCredentials: true });
      setNotifications(notifications.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const respondToNotification = async (notificationId, action) => {
    try {
      const response = await axios.post(
        `${API}/notifications/${notificationId}/respond`, 
        { action }, 
        { withCredentials: true }
      );
      setNotifications(notifications.filter(n => n.notification_id !== notificationId));
      toast.success(response.data.message);
      
      // Rafraîchir la liste des souhaits si "already_have" pour mettre à jour le statut
      if (action === "already_have") {
        fetchWishes();
      }
    } catch (error) {
      toast.error("Erreur lors de la réponse");
    }
  };

  const filterWishes = (priority) => {
    setSelectedFilter(priority);
    if (priority === "all") {
      setFilteredWishes(wishes);
    } else {
      setFilteredWishes(wishes.filter(wish => wish.priority === parseInt(priority)));
    }
  };

  const handleLinkChange = async (url) => {
    setFormData({...formData, link: url});
    
    if (!url || url.length < 10) {
      setLinkPreview(null);
      return;
    }
    
    // Simple URL validation
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return;
    }
    
    setPreviewLoading(true);
    toast.info("Récupération des informations...");
    
    try {
      const response = await axios.post(`${API}/wishes/preview`, { url }, { withCredentials: true });
      setLinkPreview(response.data);
      
      // Auto-populate form fields with scraped data
      setFormData(prev => ({
        ...prev,
        item_name: response.data.og_title || prev.item_name, // Auto-fill product name
        og_image: response.data.og_image,
        og_title: response.data.og_title,
        og_price: response.data.og_price
      }));
      
      if (response.data.og_title || response.data.og_image) {
        toast.success("✨ Produit trouvé !");
      } else {
        toast.info("😅 Ce site est trop protégé (Decathlon, Amazon...). Entre les infos manuellement !", { duration: TOAST_LONG_DURATION });
      }
    } catch (error) {
      toast.error("Impossible de charger l'aperçu");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success("À bientôt !");
      navigate('/login');
    } catch (error) {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation de la catégorie
    if (!formData.category) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }
    
    // Validation du Kiffomètre
    if (!formData.priority) {
      toast.error("Veuillez sélectionner un niveau de kiff !");
      return;
    }
    
    // Validation de la taille pour les catégories vêtements/chaussures
    if (CLOTHING_CATEGORIES.includes(formData.category) && !formData.size) {
      toast.error("Veuillez indiquer la taille / pointure");
      return;
    }
    
    try {
      if (editingWish) {
        await axios.put(`${API}/wishes/${editingWish.wish_id}`, formData, { withCredentials: true });
        toast.success("Souhait modifié !");
      } else {
        await axios.post(`${API}/wishes`, formData, { withCredentials: true });
        toast.success("Souhait ajouté !");
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchWishes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (wishId) => {
    if (!window.confirm("Supprimer ce souhait ?")) return;
    
    try {
      await axios.delete(`${API}/wishes/${wishId}`, { withCredentials: true });
      toast.success("Souhait supprimé");
      fetchWishes();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleMarkReceived = async (wishId, currentStatus) => {
    const newStatus = currentStatus === "received" ? "available" : "received";
    
    try {
      await axios.put(`${API}/wishes/${wishId}`, { status: newStatus }, { withCredentials: true });
      toast.success(newStatus === "received" ? "Marqué comme reçu" : "Marqué comme non reçu");
      fetchWishes();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/wishlist/${user.username}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Lien copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  const openEditDialog = (wish) => {
    setEditingWish(wish);
    setFormData({
      item_name: wish.item_name,
      category: wish.category,
      size: wish.size || "",
      priority: wish.priority,
      link: wish.link || "",
      og_image: wish.og_image,
      og_title: wish.og_title,
      og_price: wish.og_price
    });
    if (wish.og_image || wish.og_title || wish.og_price) {
      setLinkPreview({
        og_image: wish.og_image,
        og_title: wish.og_title,
        og_price: wish.og_price
      });
    }
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingWish(null);
    setLinkPreview(null);
    setFormData({
      item_name: "",
      category: "",
      size: "",
      priority: null,
      link: "",
      og_image: null,
      og_title: null,
      og_price: null
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <header className="bg-card/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Gift className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="font-heading text-xl font-bold" data-testid="user-name">Bonjour, {user?.name?.split(' ')[0]}</h1>
          </div>
          <div className="flex gap-2">
            <Button 
              data-testid="profile-btn"
              onClick={() => navigate('/profile')} 
              variant="outline" 
              size="sm"
              className="rounded-full"
            >
              Mon Compte
            </Button>
            <Button 
              data-testid="logout-btn"
              onClick={handleLogout} 
              variant="ghost" 
              size="sm"
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Notifications */}
        {notifications.length > 0 && (
          <div className="mb-8 space-y-3" data-testid="notifications-container">
            {notifications.map((notif) => (
              <Card 
                key={notif.notification_id} 
                className="border-l-4 border-l-pink-500 bg-gradient-to-r from-pink-50 to-amber-50 dark:from-pink-900/20 dark:to-amber-900/20"
                data-testid={`notification-${notif.notification_id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <span className="text-2xl">👀</span>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{notif.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          🎁 {notif.wish_name}
                        </p>
                        <div className="flex gap-2 mt-3">
                          <Button
                            data-testid={`still-want-${notif.notification_id}`}
                            onClick={() => respondToNotification(notif.notification_id, "still_want")}
                            size="sm"
                            className="rounded-full bg-pink-500 hover:bg-pink-600 text-white"
                          >
                            😍 Je le veux toujours !
                          </Button>
                          <Button
                            data-testid={`already-have-${notif.notification_id}`}
                            onClick={() => respondToNotification(notif.notification_id, "already_have")}
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                          >
                            🛍️ Je l'ai déjà
                          </Button>
                        </div>
                      </div>
                    </div>
                    <Button
                      data-testid={`dismiss-notification-${notif.notification_id}`}
                      onClick={() => dismissNotification(notif.notification_id)}
                      variant="ghost"
                      size="sm"
                      className="rounded-full h-8 w-8 p-0 flex-shrink-0 opacity-50 hover:opacity-100"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Share Link Card */}
        <Card className="mb-8 shadow-card max-w-md mx-auto" data-testid="share-link-card">
          <CardContent className="py-6">
            <p className="font-heading font-bold text-center mb-4">Partage ta liste de rêves</p>
            <div className="flex gap-2 items-center justify-center">
              <Input 
                readOnly 
                value={`${window.location.origin}/wishlist/${user?.username}`}
                className="font-mono text-sm max-w-[180px] truncate text-center"
                data-testid="share-link-input"
              />
              <Button 
                data-testid="copy-link-btn"
                onClick={copyShareLink}
                className="rounded-full px-4"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button 
                data-testid="whatsapp-share-btn"
                onClick={() => {
                  const link = `${window.location.origin}/wishlist/${user?.username}`;
                  const message = `Hey ! Voici ma liste de cadeaux 🎁 ${link}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                }}
                className="rounded-full px-4 bg-green-500 hover:bg-green-600"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Add Wish Button */}
        <div className="mb-6">
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-wish-btn"
                className="rounded-full px-6 py-6 font-bold shadow-lg" 
                size="lg"
              >
                <Plus className="w-5 h-5 mr-2" />
                Ajouter un souhait
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-wish-dialog" className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingWish ? "Éditer le souhait" : "Nouveau souhait"}
                </DialogTitle>
                <DialogDescription>
                  {editingWish ? "Modifiez votre souhait" : "Ajoutez un cadeau à votre liste"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="link">🔗 Lien vers l'article (recommandé)</Label>
                  <div className="relative">
                    <Input
                      data-testid="link-input"
                      id="link"
                      type="url"
                      value={formData.link}
                      onChange={(e) => handleLinkChange(e.target.value)}
                      placeholder="https://example.com/produit"
                      className="mt-1"
                    />
                    {previewLoading && (
                      <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-primary" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    📸 Collez un lien et les infos se rempliront automatiquement !
                  </p>
                </div>

                <div>
                  <Label htmlFor="priority">Le Kiffomètre 🔥</Label>
                  <Select 
                    value={formData.priority ? formData.priority.toString() : ""} 
                    onValueChange={(value) => setFormData({...formData, priority: parseInt(value)})}
                  >
                    <SelectTrigger data-testid="priority-select" className="mt-1">
                      <SelectValue placeholder="À quel point tu le veux ?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">J'en rêve même la nuit 🔥</SelectItem>
                      <SelectItem value="2">Ça me ferait plaisir 😄</SelectItem>
                      <SelectItem value="1">Un petit kiff 😊</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {linkPreview && (linkPreview.og_image || linkPreview.og_title) && (
                  <div className="border-2 border-primary/20 rounded-lg p-3 bg-primary/5 animate-in fade-in slide-in-from-top-2 duration-500" data-testid="link-preview">
                    <p className="text-sm font-bold text-primary mb-2 flex items-center gap-2">
                      <span className="text-lg">✨</span>
                      Aperçu du produit
                    </p>
                    {linkPreview.og_image && (
                      <div className="relative">
                        <img 
                          src={linkPreview.og_image} 
                          alt="Preview" 
                          className="w-full h-40 object-cover rounded-lg mb-2 border border-primary/10"
                          onError={(e) => {
                            // Si l'image ne charge pas, on affiche un message
                            e.target.style.display = 'none';
                            const fallback = e.target.parentElement.querySelector('.fallback-message');
                            if (fallback) fallback.style.display = 'block';
                          }}
                        />
                        <div className="fallback-message hidden text-xs text-muted-foreground p-2 bg-muted rounded">
                          Image non disponible - Une icône s'affichera à la place
                        </div>
                      </div>
                    )}
                    {linkPreview.og_title && (
                      <p className="text-sm font-medium">{linkPreview.og_title}</p>
                    )}
                    {linkPreview.og_price && (
                      <p className="text-sm text-primary font-bold mt-1">{linkPreview.og_price}</p>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="item_name">Nom du cadeau</Label>
                  <Input
                    data-testid="wish-name-input"
                    id="item_name"
                    value={formData.item_name}
                    onChange={(e) => setFormData({...formData, item_name: e.target.value})}
                    required
                    className="mt-1"
                    placeholder="Ex: Nike Air Max 90"
                  />
                  {linkPreview?.og_title && formData.item_name === linkPreview.og_title && (
                    <p className="text-xs text-primary mt-1 flex items-center gap-1">
                      <span>✓</span> Rempli automatiquement
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="category">Catégorie</Label>
                  <Select 
                    value={formData.category || ""} 
                    onValueChange={(value) => setFormData({...formData, category: value})}
                  >
                    <SelectTrigger data-testid="category-select" className="mt-1">
                      <SelectValue placeholder="Choisir une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {CLOTHING_CATEGORIES.includes(formData.category) && (
                  <div>
                    <Label htmlFor="size">Taille / Pointure</Label>
                    <Input
                      data-testid="size-input"
                      id="size"
                      value={formData.size}
                      onChange={(e) => setFormData({...formData, size: e.target.value})}
                      placeholder="Ex: M, 42"
                      className="mt-1"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="og_price">Prix (optionnel)</Label>
                  <div className="relative mt-1">
                    <Input
                      data-testid="price-input"
                      id="og_price"
                      value={formData.og_price || ""}
                      onChange={(e) => setFormData({...formData, og_price: e.target.value})}
                      placeholder="Ex: 49,99"
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">€</span>
                  </div>
                  {linkPreview?.og_price && formData.og_price === linkPreview.og_price && (
                    <p className="text-xs text-primary mt-1 flex items-center gap-1">
                      <span>✓</span> Rempli automatiquement
                    </p>
                  )}
                </div>

                <Button 
                  data-testid="submit-wish-btn"
                  type="submit" 
                  className="w-full rounded-full py-6 font-bold"
                >
                  {editingWish ? "Modifier" : "Ajouter"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter Buttons */}
        <div className="mb-6 flex gap-3 flex-wrap">
          <Button
            data-testid="filter-all"
            onClick={() => filterWishes("all")}
            variant={selectedFilter === "all" ? "default" : "outline"}
            className="rounded-full"
          >
            Tous mes souhaits
          </Button>
          <Button
            data-testid="filter-3"
            onClick={() => filterWishes("3")}
            variant={selectedFilter === "3" ? "default" : "outline"}
            className="rounded-full"
          >
            J'en rêve 🔥
          </Button>
          <Button
            data-testid="filter-2"
            onClick={() => filterWishes("2")}
            variant={selectedFilter === "2" ? "default" : "outline"}
            className="rounded-full"
          >
            Ça me ferait plaisir 😄
          </Button>
          <Button
            data-testid="filter-1"
            onClick={() => filterWishes("1")}
            variant={selectedFilter === "1" ? "default" : "outline"}
            className="rounded-full"
          >
            Un petit kiff 😊
          </Button>
        </div>

        {/* Wishes Grid */}
        {filteredWishes.length === 0 ? (
          <Card className="shadow-card" data-testid="empty-state">
            <CardContent className="py-12 text-center">
              <Gift className="w-16 h-16 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-bold mb-2">Aucun souhait pour le moment</h3>
              <p className="text-muted-foreground">Commencez par ajouter votre premier souhait !</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWishes.map((wish) => {
              const CategoryIcon = getCategoryIcon(wish.category);
              const hasImage = wish.og_image;
              
              return (
                <Card 
                  key={wish.wish_id} 
                  className="shadow-card hover:shadow-float transition-all duration-300 group relative overflow-hidden"
                  data-testid={`wish-card-${wish.wish_id}`}
                >
                  {hasImage ? (
                    <div className="relative h-48 w-full overflow-hidden bg-muted">
                      <img 
                        src={wish.og_image} 
                        alt={wish.item_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          // Si l'image ne charge pas, afficher l'icône cadeau
                          e.target.style.display = 'none';
                          const fallback = e.target.parentElement.querySelector('.gift-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div className="gift-fallback hidden w-full h-full items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                        <Gift className="w-20 h-20 text-primary" strokeWidth={1.5} />
                      </div>
                      {wish.priority && PRIORITY_LABELS[wish.priority] && (
                        <span className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-bold ${PRIORITY_LABELS[wish.priority].color}`}>
                          {PRIORITY_LABELS[wish.priority].label}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="relative h-48 w-full overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                      <Gift className="w-20 h-20 text-primary" strokeWidth={1.5} />
                      {wish.priority && PRIORITY_LABELS[wish.priority] && (
                        <span className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-bold ${PRIORITY_LABELS[wish.priority].color}`}>
                          {PRIORITY_LABELS[wish.priority].label}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <CardTitle className="font-heading text-lg" data-testid={`wish-title-${wish.wish_id}`}>
                          {wish.item_name}
                        </CardTitle>
                        {wish.og_price && (
                          <p className="text-sm text-primary font-bold mt-1">
                            {wish.og_price.includes('€') ? wish.og_price : `${wish.og_price} €`}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground mt-1">{wish.category}</p>
                        {wish.size && (
                          <p className="text-sm text-muted-foreground">Taille: {wish.size}</p>
                        )}
                        {wish.link && (
                          <a 
                            href={wish.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                            data-testid={`wish-link-${wish.wish_id}`}
                          >
                            <ExternalLink className="w-3 h-3" />
                            Voir l'article
                          </a>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`edit-wish-${wish.wish_id}`}
                        onClick={() => openEditDialog(wish)}
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-full"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        data-testid={`received-wish-${wish.wish_id}`}
                        onClick={() => handleMarkReceived(wish.wish_id, wish.status)}
                        variant={wish.status === "received" ? "default" : "outline"}
                        size="sm"
                        className="flex-1 rounded-full"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        {wish.status === "received" ? "Reçu" : "Recevoir"}
                      </Button>
                      <Button
                        data-testid={`delete-wish-${wish.wish_id}`}
                        onClick={() => handleDelete(wish.wish_id)}
                        variant="destructive"
                        size="sm"
                        className="rounded-full"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}